import { Combobox } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCallback, useEffect, useRef, useState } from "react";

import { PermissionRow } from "@/components/settings/crm/permission-row";
import { cn } from "@/lib/utils";
import { useUser } from "@/providers/user";
import { CRMPermissionsService } from "@/services/crm-permissions";
import { CRMObject, ObjectProfilePermission } from "@/types/crm/permissions";
import * as LucideIcons from "lucide-react";
import { toast } from "sonner";

const PROFILES = [
    { value: "marketing", label: "Marketing" },
    { value: "finance", label: "Finance" },
    { value: "teacher", label: "Teacher" },
    { value: "prod_editor", label: "Prod editor" },
    { value: "prod_director", label: "Prod director" },
    { value: "prod_project_manager", label: "Prod project manager" },
    { value: "sales_representative", label: "Représentant commercial" },
    { value: "sales_supervisor", label: "Superviseur commercial" },
    { value: "sales_manager", label: "Manager commercial" },
    { value: "csm", label: "CSM" },
    { value: "support", label: "Support" },
    { value: "sales_admin", label: "Admin commercial" },
    { value: "system_admin", label: "Admin système" },
    { value: "default", label: "Par défaut" }
];

type ViewMode = "by_object" | "by_profile";

const DEFAULT_PERMISSION = (referenceType: number, profile: string): ObjectProfilePermission => ({
    id: 0,
    reference_type: referenceType,
    profile,
    action_list: "none",
    action_retrieve: "none",
    action_create: "none",
    action_update: "none",
    action_delete: "none",
    custom_actions: [],
    use_hierarchy: false,
    fields: [],
    created_at: "",
    updated_at: ""
});

export function CrmPermissionsPage() {
    const { has_any_profile } = useUser();
    const [viewMode, setViewMode] = useState<ViewMode>("by_object");
    const [objects, setObjects] = useState<CRMObject[]>([]);
    const [selectedObject, setSelectedObject] = useState<string>("");
    const [selectedProfile, setSelectedProfile] = useState<string>("");
    const [permissions, setPermissions] = useState<ObjectProfilePermission[]>([]);
    const [loading, setLoading] = useState(false);
    const [objectsLoading, setObjectsLoading] = useState(true);
    const loadRequestIdRef = useRef(0);

    const loadObjects = useCallback(async () => {
        setObjectsLoading(true);
        try {
            const objectsData = await CRMPermissionsService.getObjects();
            setObjects(objectsData);
        } catch (error) {
            toast.error("Erreur lors du chargement des objets");
        } finally {
            setObjectsLoading(false);
        }
    }, []);

    const loadPermissions = useCallback(async () => {
        if (viewMode === "by_object" && !selectedObject) return;
        if (viewMode === "by_profile" && !selectedProfile) return;

        const requestId = ++loadRequestIdRef.current;
        setLoading(true);
        try {
            const permissionsData =
                viewMode === "by_object"
                    ? await CRMPermissionsService.getPermissions(parseInt(selectedObject))
                    : await CRMPermissionsService.getPermissionsByProfile(selectedProfile);
            if (requestId !== loadRequestIdRef.current) return;
            setPermissions(permissionsData);
        } catch (error) {
            if (requestId !== loadRequestIdRef.current) return;
            toast.error("Erreur lors du chargement des permissions");
        } finally {
            if (requestId === loadRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [viewMode, selectedObject, selectedProfile]);

    useEffect(() => {
        loadObjects();
    }, [loadObjects]);

    useEffect(() => {
        setPermissions([]);
        if ((viewMode === "by_object" && selectedObject) || (viewMode === "by_profile" && selectedProfile)) {
            loadPermissions();
        }
    }, [loadPermissions, viewMode, selectedObject, selectedProfile]);

    // Vérifier les permissions après les hooks
    if (!has_any_profile(["system_admin"])) {
        return (
            <div className="flex h-64 items-center justify-center">
                <p className="text-muted-foreground">Accès non autorisé</p>
            </div>
        );
    }

    const handlePermissionUpdate = () => {
        loadPermissions();
    };

    const getAllPermissions = (): ObjectProfilePermission[] => {
        if (viewMode === "by_object") {
            const existingPermissions = new Map(permissions.map(p => [p.profile, p]));

            return PROFILES.map(profile => {
                const existing = existingPermissions.get(profile.value);
                if (existing) {
                    return { ...existing, fields: existing.fields || [] };
                }
                return DEFAULT_PERMISSION(parseInt(selectedObject), profile.value);
            });
        }

        const existingPermissions = new Map(permissions.map(p => [p.reference_type, p]));

        return objects.map(obj => {
            const referenceTypeId = parseInt(obj.reference_type_id);
            const existing = existingPermissions.get(referenceTypeId);
            if (existing) {
                return { ...existing, fields: existing.fields || [] };
            }
            return DEFAULT_PERMISSION(referenceTypeId, selectedProfile);
        });
    };

    const selectedObjectData = objects.find(obj => obj.reference_type_id === selectedObject);
    const selectedProfileData = PROFILES.find(p => p.value === selectedProfile);

    const IconComponent = selectedObjectData?.icon && (LucideIcons as any)[selectedObjectData.icon];
    const IconColor = selectedObjectData?.color || "bg-gray-500";

    const hasSelection = viewMode === "by_object" ? !!selectedObject : !!selectedProfile;
    const firstColumnLabel = viewMode === "by_object" ? "Profile" : "Object";

    return (
        <div className=" space-y-6">
            <div className="flex flex-col gap-4 rounded-md bg-white p-6 shadow-md">
                <div>
                    <h1 className="text-md font-bold">Permissions by profile</h1>
                    <p className="text-sm text-muted-foreground">Configure the access permissions to CRM objects according to user profiles.</p>
                </div>

                <Tabs value={viewMode} onValueChange={value => setViewMode(value as ViewMode)}>
                    <TabsList>
                        <TabsTrigger value="by_object">By object</TabsTrigger>
                        <TabsTrigger value="by_profile">By profile</TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            {viewMode === "by_object" ? (
                                <Combobox
                                    name="crm_object"
                                    options={objects.map(obj => ({ value: obj.reference_type_id, label: obj.label }))}
                                    value={selectedObject}
                                    onValueChange={setSelectedObject}
                                    placeholder="Select a CRM object"
                                    commandPlaceholder="Search a CRM object"
                                    className="w-full"
                                />
                            ) : (
                                <Select value={selectedProfile} onValueChange={setSelectedProfile}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a profile" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PROFILES.map(profile => (
                                            <SelectItem key={profile.value} value={profile.value}>
                                                {profile.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {hasSelection && (
                <div className="flex flex-col gap-4 rounded-md bg-white p-6 shadow-md">
                    <div className="flex flex-row items-center gap-2">
                        {viewMode === "by_object" ? (
                            <>
                                <div
                                    className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-between rounded")}
                                    style={{ backgroundColor: IconColor }}
                                >
                                    {IconComponent && <IconComponent className="mx-auto flex h-5 w-5 text-center text-white" strokeWidth={1.5} />}
                                </div>
                                <div className="text-md w-full truncate pr-4 font-bold text-gray-800">{selectedObjectData?.label} permissions</div>
                            </>
                        ) : (
                            <div className="text-md w-full truncate pr-4 font-bold text-gray-800">{selectedProfileData?.label} permissions</div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        {loading || (viewMode === "by_profile" && objectsLoading) ? (
                            <div className="flex h-32 items-center justify-center">
                                <p className="text-muted-foreground">Loading...</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead className="rounded-md bg-gray-100">
                                        <tr>
                                            <th className="rounded-l px-4 py-2 text-left text-sm font-medium">{firstColumnLabel}</th>
                                            <th className=" px-4 py-2 text-center text-sm font-medium">Fields</th>
                                            <th className=" px-4 py-2 text-center text-sm font-medium">List</th>
                                            <th className=" px-4 py-2 text-center text-sm font-medium">Details</th>
                                            <th className=" px-4 py-2 text-center text-sm font-medium">Create</th>
                                            <th className=" px-4 py-2 text-center text-sm font-medium">Edit</th>
                                            <th className=" px-4 py-2 text-center text-sm font-medium">Delete</th>
                                            <th className="rounded-r px-4 py-2 text-center text-sm font-medium">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getAllPermissions().map(permission => {
                                            const objectData =
                                                viewMode === "by_object"
                                                    ? selectedObjectData
                                                    : objects.find(obj => parseInt(obj.reference_type_id) === permission.reference_type);

                                            return (
                                                <PermissionRow
                                                    key={viewMode === "by_object" ? permission.profile : permission.reference_type}
                                                    permission={permission}
                                                    referenceTypeId={permission.reference_type}
                                                    onUpdate={handlePermissionUpdate}
                                                    objectData={objectData}
                                                    viewMode={viewMode}
                                                />
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
